import router from "@/router";
import useTeamChat from "./useTeamChat";
import { nextTick, ref } from "@vue/composition-api";
import axios from "@/libs/axios";
import { getUserData } from "@/auth/getUserData";
import { reverse } from "lodash";
import { v4 as uuidv4 } from "uuid";

const activeChat = ref({});
const isActiveChatLoading = ref(false);
const isActiveChatScrolling = ref(false);
const refActiveTeamChatLogPS = ref(null);

const perPage = 50;
// codes coresponds to [enter, unidentified, tab, alt, shift]
const typingExcludeButtons = [13, 0, 9, 18, 16];
const chatEntryInput = ref({
    message: "",
    attachments: [],
});
const isSendPending = ref(false);
const fileUploads = ref([]);
const typingInterval = ref(false);
const isTyping = ref(false);
const isLoadingFinished = ref(false);
const page = ref(1);
let isTypingTimeout;

export default function useActiveTeamChat(sendWebsocketsMessage) {
    const getUsersTyping = (chat) => {
        return chat.usersTyping?.map((user) => user.name).join(`, `);
    };

    const scrollToBottomInActiveChatLog = () => {
        const scrollEl = refActiveTeamChatLogPS.value?.$el || refActiveTeamChatLogPS.value;
        if (!scrollEl) {
            return;
        }
        scrollEl.scrollTop = scrollEl.scrollHeight;
    };

    const loadActiveChat = async () => {
        isActiveChatLoading.value = true;
        isActiveChatScrolling.value = true;

        const { fetchChat, updateEntryStatus } = useTeamChat();
        let { chats } = useTeamChat();

        let newChat = false;

        let chat = chats.value.find((chat) => chat.id === Number(router.currentRoute.params.chatId));

        if (!chat) {
            newChat = true;
            chat = await fetchChat(Number(router.currentRoute.params.chatId));
            chat.usersTyping = [];
            chat.lastMessage = "";
            chat.unreadMessages = 0;
        }

        const participants = await loadUsersAndParticipants(chat);
        chat.participants = participants;

        const participant = chat.participants.find((participant) => participant.userId === getUserData().id);
        const leftAt = participant?.leftAt;
        participant.chatStatus = "read";
        if (leftAt) {
            chat.isArchived = true;
        }

        resetSendingMessage();

        await loadChatLog(chat, participant?.id);

        if (newChat && Array.isArray(chats.value) && !chats.value.find((row) => row.id === chat.id)) {
            // chats loaded, but the new chat is not yet on the list
            chats.value.push(chat);
        }

        updateEntryStatus("read", chat.id, participant.id);

        isActiveChatLoading.value = false;
        isLoadingFinished.value = false;
        page.value = 1;
        // wait until the chat entries can manage to properly render and scroll bottom until the infinite load is active
        setTimeout(() => {
            isActiveChatScrolling.value = false;
        }, 3000);
    };

    const loadUsersAndParticipants = async (chat) => {
        const response = await axios.get(`/team-chats/${chat.id}/participants`);
        let participants = response.data;

        if (participants.length) {
            const users = await axios.get(`/users`, {
                params: {
                    s: {
                        $or: participants.map((participant) => ({ id: participant.userId })),
                    },
                },
            });

            participants = participants.map((participant) => ({
                ...participant,
                user: users.data.find((user) => user.id === participant.userId),
            }));
        }

        return participants;
    };

    const loadChatLog = async (chat, participantId) => {
        const response = await axios.get(`/team-chats/${chat.id}/participants/${participantId}/entries`, {
            params: {
                page: 1,
                limit: perPage,
                sort: "createdAt,DESC",
            },
        });

        chat.entries = reverse(response.data);
        chat.unreadMessages = 0;
        chat.usersTyping = [];

        activeChat.value = chat;

        nextTick(() => {
            scrollToBottomInActiveChatLog();
        });
    };

    const loadPage = async (page, chat) => {
        isActiveChatScrolling.value = true;
        const participant = chat.participants.find((participant) => participant.userId === getUserData().id);
        const response = await axios.get(`/team-chats/${chat.id}/participants/${participant.id}/entries`, {
            params: {
                page,
                limit: perPage,
                sort: "createdAt,DESC",
            },
        });

        const entries = reverse(response.data);

        // if scroll is in max top position this prevents it from sticking to the top when more entries are loaded
        if (refActiveTeamChatLogPS.value) {
            refActiveTeamChatLogPS.value.scrollTop = refActiveTeamChatLogPS.value.scrollTop + 1;
        }

        activeChat.value = {
            ...activeChat.value,
            entries: [...entries, ...activeChat.value.entries],
        };

        setTimeout(() => {
            isActiveChatScrolling.value = false;
        }, 3000);

        return response.data.length;
    };

    const leaveTeamChat = () => {
        activeChat.value = {
            ...activeChat.value,
            isArchived: true,
        };
    };

    const sendStartTyping = () => {
        isTyping.value = true;

        sendWebsocketsMessage({
            type: "teamChatStartTyping",
            payload: {
                chatId: activeChat.value.id,
                userId: getUserData().id,
                userName: getUserData().name,
                typingTime: new Date(),
            },
            rooms: [`customer-${getUserData()?.settings[0]?.customerId}`],
        });
    };

    const sendStopTyping = () => {
        isTyping.value = false;

        sendWebsocketsMessage({
            type: "teamChatStopTyping",
            payload: {
                chatId: activeChat.value.id,
                userId: getUserData().id,
                userName: getUserData().name,
            },
            rooms: [`customer-${getUserData()?.settings[0]?.customerId}`],
        });
    };

    const stopTyping = () => {
        clearInterval(typingInterval.value);
        clearTimeout(isTypingTimeout);
        typingInterval.value = false;
        sendStopTyping();
    };

    const resetSendingMessage = async () => {
        chatEntryInput.value = {
            message: "",
            attachments: [],
        };
        fileUploads.value = [];
    };

    const sendMessage = async () => {
        if (chatEntryInput.value.attachments.length === 0 && !chatEntryInput.value.message) {
            return;
        }

        const { chats } = useTeamChat();

        isSendPending.value = true;

        let tmpEntries = [];
        const senderId = getUserData().id;

        if (chatEntryInput.value.message) {
            const tmpId = uuidv4();
            tmpEntries.push({
                id: uuidv4(),
                tmpId: uuidv4(),
                chatEntryId: tmpId,
                userId: senderId,
                createdAt: new Date().toISOString(),
                status: "pending",
                senderUserId: senderId,
                message: chatEntryInput.value.message,
                type: "text",
            });
        }
        if (chatEntryInput.value.attachments && chatEntryInput.value.attachments.length > 0) {
            const tmpId = uuidv4();
            tmpEntries.push({
                attachments: chatEntryInput.value.attachments,
                type: "asset",
                id: uuidv4(),
                tmpId: uuidv4(),
                userId: senderId,
                createdAt: new Date().toISOString(),
                status: "pending",
                senderUserId: senderId,
                chatEntryId: tmpId,
            });
        }

        activeChat.value.entries = activeChat.value.entries.concat(tmpEntries);
        activeChat.value = {
            ...activeChat.value,
        };

        chatEntryInput.value = {
            message: "",
            attachments: [],
        };
        fileUploads.value = [];

        stopTyping();

        nextTick(() => {
            scrollToBottomInActiveChatLog();
        });

        await Promise.all(
            tmpEntries.map((tmpEntry) =>
                axios
                    .post(`team-chats/${activeChat.value.id}/entries`, {
                        attachments: tmpEntry.attachments?.map((attachment) => {
                            delete attachment.originalFileObject;
                            return attachment;
                        }),
                        type: tmpEntry.type,
                        message: tmpEntry.message,
                    })
                    .then((response) => {
                        tmpEntry.chatEntryId = response.data.id;
                        tmpEntry.ticks = response.data.participantEntries;
                    })
                    .catch(() => {
                        tmpEntry.status = "declined";
                    })
            )
        );

        nextTick(() => {
            isSendPending.value = false;
        });

        // --- HANDLE CHATS LIST TO ADD LAST ENTRY ---
        const chat = chats.value.find((chat) => chat.id === activeChat.value.id);

        if (!chat) {
            return;
        }

        chat.lastMessage = activeChat.value.entries[activeChat.value.entries.length - 1];
    };

    const setUserTyping = (event) => {
        //send a startTyping every 3 seconds
        if (!typingInterval.value) {
            sendStartTyping();
            typingInterval.value = setInterval(sendStartTyping(), 3000);
        }

        if (typingExcludeButtons.includes(event.keyCode)) {
            return;
        }

        //after 15 seconds of no button press stop interval and send stoppedTyping
        clearTimeout(isTypingTimeout);
        isTypingTimeout = setTimeout(() => {
            stopTyping();
        }, 15000);
    };

    const resetActiveChat = () => {
        activeChat.value = {};
    };

    return {
        activeChat,
        refActiveTeamChatLogPS,
        isActiveChatLoading,
        isActiveChatScrolling,
        chatEntryInput,
        isSendPending,
        fileUploads,
        perPage,
        page,
        isLoadingFinished,

        leaveTeamChat,
        loadActiveChat,
        loadPage,
        loadUsersAndParticipants,
        scrollToBottomInActiveChatLog,
        sendMessage,
        resetSendingMessage,
        setUserTyping,
        sendStartTyping,
        sendStopTyping,
        getUsersTyping,
        resetActiveChat,
    };
}
